
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info, Clipboard, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { SLACK_WEBHOOK_RECEIVER, SLACK_WEBHOOK_DIAGNOSTIC, SUPABASE_URL } from '@/config/environment';

interface AdvancedDetailsProps {
  aiStaffId: string;
  webhookUrl: string;
  slackToken: string;
}

export const AdvancedDetails: React.FC<AdvancedDetailsProps> = ({
  aiStaffId,
  webhookUrl,
  slackToken
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  const webhookEndpoint = SLACK_WEBHOOK_RECEIVER;
  const diagnosticEndpoint = SLACK_WEBHOOK_DIAGNOSTIC;
  const isDevMode = SUPABASE_URL.includes('localhost');
  
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: `${label} copied to clipboard`,
    });
  };
  
  return (
    <div className="mt-6">
      <Separator className="my-4" />
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {isOpen ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
          Advanced Details
        </button>
        
        {isOpen && (
          <div className="mt-4 space-y-3 text-xs">
            {isDevMode && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                <div className="text-amber-700">
                  <p className="font-medium">Development Mode Detected</p>
                  <p>You are using localhost URLs which won't work in production. Set VITE_SUPABASE_URL environment variable to your Supabase project URL.</p>
                </div>
              </div>
            )}
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2">
              <Info className="h-4 w-4 text-amber-500 mt-0.5" />
              <div className="text-amber-700">
                <p className="font-medium">For Troubleshooting Only</p>
                <p>These details may be required if you need help from support.</p>
              </div>
            </div>
            
            <div className="space-y-3 border rounded-md p-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">AI Staff ID</p>
                <div className="flex items-center gap-2">
                  <code className="bg-muted p-1.5 rounded text-xs flex-1 font-mono">{aiStaffId}</code>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 px-2" 
                    onClick={() => handleCopy(aiStaffId, 'AI Staff ID')}
                  >
                    <Clipboard className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              
              <div>
                <p className="text-xs text-muted-foreground mb-1">Webhook Endpoint</p>
                <div className="flex items-center gap-2">
                  <code className="bg-muted p-1.5 rounded text-xs flex-1 font-mono overflow-x-auto">{webhookEndpoint}</code>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 px-2" 
                    onClick={() => handleCopy(webhookEndpoint, 'Webhook Endpoint')}
                  >
                    <Clipboard className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              
              <div>
                <p className="text-xs text-muted-foreground mb-1">Diagnostic Endpoint (For Troubleshooting)</p>
                <div className="flex items-center gap-2">
                  <code className="bg-muted p-1.5 rounded text-xs flex-1 font-mono overflow-x-auto">{diagnosticEndpoint}</code>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 px-2" 
                    onClick={() => handleCopy(diagnosticEndpoint, 'Diagnostic Endpoint')}
                  >
                    <Clipboard className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <p className="mt-1 text-2xs text-primary">
                  Use this endpoint in your Slack app config for easier troubleshooting.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
